import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col flex-1 min-h-0 rounded-xl bg-white dark:bg-(--colors-background-black-default) shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] dark:shadow-[0px_0px_4px_rgba(255,255,255,0.1)] overflow-hidden" };
const _hoisted_2 = { class: "flex items-center justify-between gap-3 px-5 h-14 border-b border-gainsboro dark:border-(--colors-border-base)" };
const _hoisted_3 = { class: "flex items-center gap-2" };
const _hoisted_4 = { class: "text-base font-semibold text-(--colors-text-default)" };
const _hoisted_5 = { class: "flex-1 overflow-x-hidden min-h-0" };
const _hoisted_6 = { class: "border-t border-gainsboro dark:border-(--colors-border-base) px-5 py-3 bg-white dark:bg-(--colors-background-black-default)" };
import { computed, h, provide } from "vue";
import { useI18n } from "vue-i18n";
import { Avatar, Checkbox, DataTable, FacetedSearchDrawer, Pager, TableControls, useColumnSettings, useTableQuery, DEFAULT_TABLE_LABELS, TABLE_QUERY_KEY, TABLE_PERMISSIONS_KEY, TABLE_LABELS_KEY, } from "@eudonet/opal";
import { buildColumnSettingsLabels } from "@/shared/composables/columnSettingsLabels";
import { useTableQueryUrlSync } from "@/shared/composables/useTableQueryUrlSync";
import { useProfileStore } from "../../../../settings/stores/useProfileStore";
import { formatDateTime } from "@/shared/utils/date-formatting";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useDonationOptions } from "../../composables/useDonationOptions";
import { historyActionLabel, historyFieldLabel, historyValueLabel } from "../../composables/historyLabels";
import { useHistoryDateFormatters } from "../../composables/useHistoryDateFormatters";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationTabHistory',
    props: {
        history: {},
        loading: { type: Boolean, default: false },
        currency: { default: null }
    },
    setup(__props) {
        const props = __props;
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const { formatAmount } = useFinanceFormatters();
        const historyDateFormatters = useHistoryDateFormatters();
        // Catalog-backed enum labels, reused to translate the "Valeur" column for the
        // enum-typed audit fields (status, payment method, nature, form, source, type).
        const { statusOptions, paymentMethodLabelOptions, donationNatureOptions, donationFormOptions, donationSourceOptions, donationTypeOptions, } = useDonationOptions();
        const valueOptionsByField = computed(() => ({
            TransactionStatus: statusOptions.value,
            PaymentMethod: paymentMethodLabelOptions.value,
            DonationNature: donationNatureOptions.value,
            DonationForm: donationFormOptions.value,
            DonationSource: donationSourceOptions.value,
            DonationType: donationTypeOptions.value,
        }));
        // Without a currency the amount stays as recorded: formatAmount would turn it into a dash.
        const valueLabel = (field, value) => {
            const currency = props.currency;
            return historyValueLabel(field, value ?? "", valueOptionsByField.value, currency ? (amount) => formatAmount(amount, currency) : undefined, historyDateFormatters);
        };
        const ROW_OPTIONS = [10, 30, 50];
        // ── Table query (search, pagination) ─────────────────────────────────────────
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0] });
        // Provide the table context the OPAL sub-components inject (same contract as TableShell).
        provide(TABLE_QUERY_KEY, tableQuery);
        const tablePermissions = { canSaveView: false, canShareView: false };
        provide(TABLE_PERMISSIONS_KEY, tablePermissions);
        const labelsComputed = computed(() => ({
            ...DEFAULT_TABLE_LABELS,
            filterButton: t("table.filter"),
            eraseAll: t("table.eraseAll"),
            hideFilters: (n) => t("table.hideFilters", { count: n }),
            showFilters: (n) => t("table.showFilters", { count: n }),
            closeSearch: t("table.closeSearch"),
            removeFilter: (label) => t("table.removeFilter", { label }),
        }));
        // Cast: opal's dist types brand ComputedRef against its own bundled Vue copy; the
        // runtime value is a valid ref the sub-components unwrap. Same reason list pages
        // pass labels through TableShell rather than providing the key directly.
        provide(TABLE_LABELS_KEY, labelsComputed);
        // ── URL sync (?q, ?page) — preserves the ?tab=history param ───────────────────
        useTableQueryUrlSync({
            tableQuery,
            syncSearch: true,
            defaultPageSize: ROW_OPTIONS[0],
        });
        // ── Client-side search + pagination ──────────────────────────────────────────
        const filteredRows = computed(() => {
            const term = tableQuery.state.search.trim().toLowerCase();
            if (!term)
                return props.history;
            return props.history.filter((entry) => {
                const sourceLabel = t(`finance.donations.detail.history.source.${entry.source}`);
                // Search the displayed (localized) field/value so a query matches what the user sees.
                // Must mirror the "field" column cell: branch on `entry.field` (summary rows have an empty
                // field and show the action label), not on operation — otherwise an Updated summary row
                // (allocation/comment/receipt) would index a blank label and be unsearchable by its visible text.
                const fieldOrAction = entry.field
                    ? historyFieldLabel(t, entry.field)
                    : historyActionLabel(t, entry.entityType, entry.operation);
                return [
                    fieldOrAction,
                    valueLabel(entry.field, entry.previousValue),
                    valueLabel(entry.field, entry.value),
                    entry.author ?? "",
                    sourceLabel,
                ]
                    .join(" ")
                    .toLowerCase()
                    .includes(term);
            });
        });
        const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / tableQuery.state.pageSize)));
        const pagedRows = computed(() => {
            const start = (tableQuery.state.page - 1) * tableQuery.state.pageSize;
            return filteredRows.value.slice(start, start + tableQuery.state.pageSize);
        });
        // ── Column settings (gear → FacetedSearchDrawer) ─────────────────────────────
        const settingsColumns = () => [
            {
                key: "field",
                label: t("finance.donations.detail.history.colField"),
                visible: true,
                pinned: false,
            },
            {
                key: "value",
                label: t("finance.donations.detail.history.colValue"),
                visible: true,
                pinned: false,
            },
            {
                key: "date",
                label: t("finance.donations.detail.history.colModifiedAt"),
                visible: true,
                pinned: false,
            },
            {
                key: "source",
                label: t("finance.donations.detail.history.colSource"),
                visible: true,
                pinned: false,
            },
            {
                key: "author",
                label: t("finance.donations.detail.history.colModifiedBy"),
                visible: true,
                pinned: false,
            },
        ];
        const columnSettings = useColumnSettings({
            columns: settingsColumns,
            defaultOrder: ["field", "value", "date", "source", "author"],
            defaultPinning: { left: ["select"], right: [] },
            labels: () => buildColumnSettingsLabels(t),
        });
        // The locked "select" column is not part of the drawer settings — keep it first.
        const columnOrder = computed(() => ["select", ...columnSettings.order.value]);
        // ── Columns ──────────────────────────────────────────────────────────────────
        const columns = computed(() => [
            {
                id: "select",
                locked: true,
                draggable: false,
                resizable: false,
                sortable: false,
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                header: ({ table }) => h(Checkbox, {
                    modelValue: table.getIsAllPageRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    "onUpdate:modelValue": (v) => table.toggleAllPageRowsSelected(!!v),
                    ariaLabel: t("common.selectAll"),
                }),
                cell: ({ row }) => h(Checkbox, {
                    modelValue: row.getIsSelected(),
                    "onUpdate:modelValue": (v) => row.toggleSelected(!!v),
                    ariaLabel: t("common.selectRow"),
                }),
            },
            {
                key: "field",
                header: t("finance.donations.detail.history.colField"),
                draggable: true,
                cell: ({ row }) => {
                    const entry = row.original;
                    // Donation field-diff rows carry a concrete `field`; lifecycle/summary rows (allocation,
                    // comment, receipt — Created AND Updated) have an empty `field` and must use the action label.
                    // Branching on `field` (not `operation`) keeps allocation *Updated* rows from rendering blank.
                    const label = entry.field
                        ? historyFieldLabel(t, entry.field)
                        : historyActionLabel(t, entry.entityType, entry.operation);
                    return h("span", { class: "text-(--colors-text-default)" }, label);
                },
            },
            {
                key: "value",
                header: t("finance.donations.detail.history.colValue"),
                draggable: true,
                cell: ({ row }) => {
                    const entry = row.original;
                    if (entry.valueIcon) {
                        return h("span", {
                            class: "inline-flex h-7 w-7 items-center justify-center rounded-full bg-(--colors-background-white-secondary) dark:bg-white/5",
                        }, [h("span", { class: `${entry.valueIcon} text-base text-(--colors-text-secondary)` })]);
                    }
                    // Event rows have no field and no value to compare.
                    if (!entry.field) {
                        return h("span", { class: "text-(--colors-text-default)" }, valueLabel(entry.field, entry.value));
                    }
                    // "old → new"; an empty side (first set, cleared field) reads as a dash. The arrow is hidden
                    // from screen readers, which hear the two labelled values instead.
                    const side = (labelKey, value) => [
                        h("span", { class: "sr-only" }, `${t(labelKey)}: `),
                        h("span", null, valueLabel(entry.field, value) || "–"),
                    ];
                    return h("span", { class: "text-(--colors-text-default)" }, [
                        ...side("finance.donations.detail.history.colOldValue", entry.previousValue),
                        h("span", { "aria-hidden": "true", class: "mx-1 text-(--colors-text-secondary)" }, "→"),
                        h("span", { class: "sr-only" }, ", "),
                        ...side("finance.donations.detail.history.colNewValue", entry.value),
                    ]);
                },
            },
            {
                key: "date",
                header: t("finance.donations.detail.history.colModifiedAt"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "text-(--colors-text-secondary)" }, formatDateTime(row.original.date, locale.value, profileStore.profile?.dateFormat, profileStore.profile?.timeFormat)),
            },
            {
                key: "source",
                header: t("finance.donations.detail.history.colSource"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "text-(--colors-text-default)" }, t(`finance.donations.detail.history.source.${row.original.source}`)),
            },
            {
                key: "author",
                header: t("finance.donations.detail.history.colModifiedBy"),
                draggable: true,
                cell: ({ row }) => {
                    const author = row.original.author;
                    if (!author) {
                        return h("span", { class: "text-(--colors-text-secondary)" }, "–");
                    }
                    return h("div", { class: "flex items-center gap-2 min-w-0" }, [
                        h(Avatar, { name: author, size: "sm", presentational: true }),
                        h("span", { class: "truncate text-(--colors-text-default)" }, author),
                    ]);
                },
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _cache[1] || (_cache[1] = _createElementVNode("span", { class: "ri-history-line text-lg text-(--colors-text-secondary)" }, null, -1 /* CACHED */)),
                            _createElementVNode("h2", _hoisted_4, _toDisplayString(_unref(t)("finance.donations.detail.history.title")), 1 /* TEXT */)
                        ])
                    ]),
                    _createVNode(_unref(TableControls), { "show-filter": "" }),
                    _createElementVNode("div", _hoisted_5, [
                        _createVNode(_unref(DataTable), {
                            columns: columns.value,
                            data: pagedRows.value,
                            "column-visibility": _unref(columnSettings).visibility,
                            "column-order": columnOrder.value,
                            "column-pinning": _unref(columnSettings).pinning.value,
                            loading: __props.loading,
                            "has-settings": "",
                            "sticky-header": "",
                            "empty-text": _unref(t)('finance.donations.detail.history.empty'),
                            "settings-aria-label": _unref(t)('columnSettings.title'),
                            onSettingsClick: _unref(columnSettings).openDrawer,
                            "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                            "onUpdate:columnPinning": _unref(columnSettings).setPinning
                        }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "loading", "empty-text", "settings-aria-label", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning"])
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                        _createVNode(_unref(Pager), {
                            "current-page": _unref(tableQuery).state.page,
                            "total-pages": totalPages.value,
                            "total-items": filteredRows.value.length,
                            "items-per-page": _unref(tableQuery).state.pageSize,
                            "rows-per-page-options": ROW_OPTIONS,
                            "rows-per-page-label": _unref(t)('pagination.rowsPerPage'),
                            "items-count-separator": _unref(t)('pagination.of'),
                            "previous-label": _unref(t)('pagination.previous'),
                            "next-label": _unref(t)('pagination.next'),
                            "go-to-page-label": _unref(t)('pagination.goToPage'),
                            "go-label": _unref(t)('pagination.go'),
                            "show-go-to-page": "",
                            onPageChange: _unref(tableQuery).setPage,
                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                    ])
                ]),
                _createVNode(_unref(FacetedSearchDrawer), {
                    modelValue: _unref(columnSettings).drawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(columnSettings).drawerOpen.value) = $event)),
                    columns: _unref(columnSettings).settingsItems.value,
                    "default-columns": _unref(columnSettings).defaultSettingsItems.value,
                    "column-order": _unref(columnSettings).order.value,
                    labels: _unref(columnSettings).labels.value,
                    onSave: _unref(columnSettings).handleSave
                }, null, 8 /* PROPS */, ["modelValue", "columns", "default-columns", "column-order", "labels", "onSave"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
